import React from 'react';
import { Anchor, Group } from '@mantine/core';
import { IconArrowLeft } from '@tabler/icons-react';
import { Navigate, useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import AuthFrame from '@/components/auth/frame/Auth.Frame';
import AuthFlow from '@/components/auth/flow/Auth.Flow';
import useAuthFlow from '@/hooks/use.auth.flow';
import useSignedIn from '@/hooks/use.signed.in';
import flowTitleOf from '@/function/auth/flow.title.of';
import safeNextPath from '@/function/auth/auth.next.path';
import emailInputClean from '@/function/auth/email.input.clean';
import { useAppSelector } from '@/store/store.hooks';
import { selectSessionStatus, selectSessionUser } from '@/store/auth/auth.selectors';
import LoginStart from './components/Login.Start';
import { LOGIN_COPY } from './config/login.labels';

const START_SCREENS = new Set(['idle', 'password']);

const Login: React.FC = () => {
    const navigate = useNavigate();
    const [params] = useSearchParams();
    const location = useLocation();
    const status = useAppSelector(selectSessionStatus);
    const user = useAppSelector(selectSessionUser);
    const onSignedIn = useSignedIn();
    const flow = useAuthFlow({ kind: 'sign-in', onDone: onSignedIn });
    const carried = emailInputClean(String((location.state as { email?: unknown } | null)?.email ?? ''));
    const atStart = (START_SCREENS.has(flow.screen.id) && flow.choice !== 'passkey') || (flow.choice === 'password' && flow.state.phase === 'loading');

    const next = safeNextPath(params.get('next'));

    if (status === 'authenticated') return <Navigate to={next ?? user?.preferences.landingPath ?? '/overview'} replace />;

    const toForgot = (email: string) => navigate('/auth/reset', { state: { email } });

    return (
        <AuthFrame
            title={atStart ? LOGIN_COPY.title : flowTitleOf('sign-in', flow.screen, LOGIN_COPY.title)}
            subtitle={atStart ? LOGIN_COPY.subtitle : flow.hint ? `Per ${flow.hint}` : LOGIN_COPY.subtitle}
        >
            {atStart ? (
                <LoginStart flow={flow} initialEmail={carried} onForgot={toForgot} />
            ) : (
                <AuthFlow flow={flow} onRestart={() => flow.reset()} onForgot={() => toForgot(flow.hint ?? '')} />
            )}
            {!atStart && flow.screen.id !== 'done' ? (
                <Group justify="center" mt="sm">
                    <Anchor component="button" type="button" size="sm" style={{ minHeight: 44, display: 'inline-flex', alignItems: 'center', gap: 6 }} disabled={flow.busy} onClick={() => flow.reset()}>
                        <IconArrowLeft size={16} aria-hidden />
                        Usa un altro account
                    </Anchor>
                </Group>
            ) : null}
        </AuthFrame>
    );
};

export default Login;
